import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  MessageSquareWarning, MessageSquareHeart, Plus, FileSpreadsheet, 
  Upload, Download, Trash2, Eye, X, FileText, Loader2, Filter,
  CheckCircle, XCircle, User, Phone, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComplaintsPage = () => {
  const [activeTab, setActiveTab] = useState('complaints');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Data
  const [complaints, setComplaints] = useState([]);
  const [compliments, setCompliments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [registrationFilter, setRegistrationFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  
  // Dialogs
  const [showComplaintDialog, setShowComplaintDialog] = useState(false);
  const [showComplimentDialog, setShowComplimentDialog] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingDocuments, setViewingDocuments] = useState(null);
  
  // Form data
  const [complaintForm, setComplaintForm] = useState({
    date: new Date().toISOString().split('T')[0],
    complaint_full: '',
    complaint_short: '',
    registration_number: '',
    driver_id: '',
    action_by: '',
    action_taken: '',
    outcome: '',
    client_name: '',
    client_tel: '',
    contacted: false,
    satisfied: false
  });
  
  const [complimentForm, setComplimentForm] = useState({
    date: new Date().toISOString().split('T')[0],
    compliment_full: '',
    compliment_short: '',
    registration_number: '',
    driver_id: '',
    action_by: '',
    actioned: false,
    rewards: '',
    client_name: '',
    client_tel: '',
    contacted: false,
    satisfied: false
  });
  
  // Document upload
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('evidence');
  const fileInputRef = React.useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (registrationFilter) params.registration = registrationFilter;
      if (driverFilter) params.driver_id = driverFilter;
      
      const [complaintsRes, complimentsRes, driversRes, regsRes, statsRes] = await Promise.all([
        api.get('/complaints/complaints', { params }),
        api.get('/complaints/compliments', { params }),
        api.get('/drivers'),
        api.get('/complaints/registrations'),
        api.get('/complaints/stats')
      ]);
      
      setComplaints(complaintsRes.data || []);
      setCompliments(complimentsRes.data || []);
      setDrivers(driversRes.data || []);
      setRegistrations(regsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateFrom, dateTo, registrationFilter, driverFilter]);

  const getDriverName = (driverId) => {
    if (!driverId) return '-';
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return '-';
    const fn = driver.first_name || driver.user?.first_name || '';
    const ln = driver.last_name || driver.user?.last_name || '';
    return `${fn} ${ln}`.trim() || driver.employee_number || '-';
  };

  const resetComplaintForm = () => {
    setComplaintForm({
      date: new Date().toISOString().split('T')[0],
      complaint_full: '',
      complaint_short: '',
      registration_number: '',
      driver_id: '',
      action_by: '',
      action_taken: '',
      outcome: '',
      client_name: '',
      client_tel: '',
      contacted: false,
      satisfied: false
    });
    setEditingRecord(null);
  };

  const resetComplimentForm = () => {
    setComplimentForm({
      date: new Date().toISOString().split('T')[0],
      compliment_full: '',
      compliment_short: '',
      registration_number: '',
      driver_id: '',
      action_by: '',
      actioned: false,
      rewards: '',
      client_name: '',
      client_tel: '',
      contacted: false,
      satisfied: false
    });
    setEditingRecord(null);
  };

  const handleSubmitComplaint = async () => {
    if (!complaintForm.complaint_short || !complaintForm.complaint_full) {
      toast.error('Please fill in complaint details');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingRecord) {
        await api.put(
          `/complaints/complaint/${editingRecord.id}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
          complaintForm
        );
        toast.success('Complaint updated');
      } else {
        await api.post(
          `/complaints/complaint?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
          complaintForm
        );
        toast.success('Complaint created with corrective action');
      }
      
      setShowComplaintDialog(false);
      resetComplaintForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save complaint:', error);
      toast.error('Failed to save complaint');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCompliment = async () => {
    if (!complimentForm.compliment_short || !complimentForm.compliment_full) {
      toast.error('Please fill in compliment details');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingRecord) {
        await api.put(
          `/complaints/compliment/${editingRecord.id}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
          complimentForm
        );
        toast.success('Compliment updated');
      } else {
        await api.post(
          `/complaints/compliment?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
          complimentForm
        );
        toast.success('Compliment created with corrective action for rewards');
      }
      
      setShowComplimentDialog(false);
      resetComplimentForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save compliment:', error);
      toast.error('Failed to save compliment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComplaint = (complaint) => {
    setEditingRecord(complaint);
    setComplaintForm({
      date: complaint.date || '',
      complaint_full: complaint.complaint_full || '',
      complaint_short: complaint.complaint_short || '',
      registration_number: complaint.registration_number || '',
      driver_id: complaint.driver_id || '',
      action_by: complaint.action_by || '',
      action_taken: complaint.action_taken || '',
      outcome: complaint.outcome || '',
      client_name: complaint.client_name || '',
      client_tel: complaint.client_tel || '',
      contacted: complaint.contacted || false,
      satisfied: complaint.satisfied || false
    });
    setShowComplaintDialog(true);
  };

  const handleEditCompliment = (compliment) => {
    setEditingRecord(compliment);
    setComplimentForm({
      date: compliment.date || '',
      compliment_full: compliment.compliment_full || '',
      compliment_short: compliment.compliment_short || '',
      registration_number: compliment.registration_number || '',
      driver_id: compliment.driver_id || '',
      action_by: compliment.action_by || '',
      actioned: compliment.actioned || false,
      rewards: compliment.rewards || '',
      client_name: compliment.client_name || '',
      client_tel: compliment.client_tel || '',
      contacted: compliment.contacted || false,
      satisfied: compliment.satisfied || false
    });
    setShowComplimentDialog(true);
  };

  const handleDeleteComplaint = async (id) => {
    if (!window.confirm('Are you sure you want to delete this complaint?')) return;
    
    try {
      await api.delete(`/complaints/complaint/${id}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`);
      toast.success('Complaint deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete complaint:', error);
      toast.error('Failed to delete complaint');
    }
  };

  const handleDeleteCompliment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this compliment?')) return;
    
    try {
      await api.delete(`/complaints/compliment/${id}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`);
      toast.success('Compliment deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete compliment:', error);
      toast.error('Failed to delete compliment');
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !viewingDocuments) return;
    
    try {
      setUploadingDoc(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const recordType = viewingDocuments.type;
      const recordId = viewingDocuments.record.id;
      
      await api.post(
        `/complaints/${recordType}/${recordId}/document?document_type=${selectedDocType}&user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Document uploaded');
      fetchData();
      
      // Refresh the viewing record
      const endpoint = recordType === 'complaint' ? '/complaints/complaints' : '/complaints/compliments';
      const res = await api.get(endpoint);
      const updatedRecord = (res.data || []).find(r => r.id === recordId);
      if (updatedRecord) {
        setViewingDocuments({ ...viewingDocuments, record: updatedRecord });
      }
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownloadDocument = (recordType, recordId, docId, filename) => {
    const url = `${API_URL}/api/complaints/${recordType}/${recordId}/document/${docId}`;
    window.open(url, '_blank');
  };

  const handleDeleteDocument = async (recordType, recordId, docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(
        `/complaints/${recordType}/${recordId}/document/${docId}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`
      );
      toast.success('Document deleted');
      
      // Refresh the viewing record
      const endpoint = recordType === 'complaint' ? '/complaints/complaints' : '/complaints/compliments';
      const res = await api.get(endpoint);
      const updatedRecord = (res.data || []).find(r => r.id === recordId);
      if (updatedRecord) {
        setViewingDocuments({ ...viewingDocuments, record: updatedRecord });
      }
      fetchData();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleExport = (type) => {
    let url = `${API_URL}/api/complaints/export/${type}`;
    const params = [];
    if (dateFrom) params.push(`date_from=${dateFrom}`);
    if (dateTo) params.push(`date_to=${dateTo}`);
    if (params.length > 0) url += '?' + params.join('&');
    window.open(url, '_blank');
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'ca_document': 'CA Document',
      'action_document': 'Action Document',
      'evidence': 'Evidence',
      'other': 'Other'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="complaints-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">COMPLAINTS & COMPLIMENTS</h1>
          <p className="text-slate-600">Manage customer feedback and driver recognition</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => { resetComplaintForm(); setShowComplaintDialog(true); }}
            className="gap-2 bg-red-600 hover:bg-red-700"
            data-testid="new-complaint-btn"
          >
            <MessageSquareWarning className="w-4 h-4" />
            New Complaint
          </Button>
          <Button 
            onClick={() => { resetComplimentForm(); setShowComplimentDialog(true); }}
            className="gap-2 bg-green-600 hover:bg-green-700"
            data-testid="new-compliment-btn"
          >
            <MessageSquareHeart className="w-4 h-4" />
            New Compliment
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-700 mb-1">
                <MessageSquareWarning className="w-4 h-4" />
                <span className="text-sm font-medium">Complaints</span>
              </div>
              <p className="text-2xl font-bold text-red-900">{stats.complaints?.total || 0}</p>
              <p className="text-xs text-red-600">{stats.complaints?.open || 0} open</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <MessageSquareHeart className="w-4 h-4" />
                <span className="text-sm font-medium">Compliments</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{stats.compliments?.total || 0}</p>
              <p className="text-xs text-green-600">{stats.compliments?.pending_rewards || 0} pending rewards</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-orange-700 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">This Month</span>
              </div>
              <p className="text-2xl font-bold text-orange-900">{stats.complaints?.this_month || 0}</p>
              <p className="text-xs text-orange-600">complaints</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-emerald-700 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">This Month</span>
              </div>
              <p className="text-2xl font-bold text-emerald-900">{stats.compliments?.this_month || 0}</p>
              <p className="text-xs text-emerald-600">compliments</p>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Resolved</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{stats.complaints?.resolved || 0}</p>
              <p className="text-xs text-blue-600">satisfied clients</p>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-700 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Rewarded</span>
              </div>
              <p className="text-2xl font-bold text-purple-900">{stats.compliments?.actioned || 0}</p>
              <p className="text-xs text-purple-600">drivers recognized</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">From:</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">To:</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Registration:</Label>
              <Select value={registrationFilter || 'all'} onValueChange={(v) => setRegistrationFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {registrations.map(reg => (
                    <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Driver:</Label>
              <Select value={driverFilter || 'all'} onValueChange={(v) => setDriverFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="All Drivers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Drivers</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {getDriverName(d.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setDateFrom(''); setDateTo(''); setRegistrationFilter(''); setDriverFilter(''); }}
              className="text-red-600"
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="complaints" className="gap-2" data-testid="complaints-tab">
              <MessageSquareWarning className="w-4 h-4" />
              Complaints ({complaints.length})
            </TabsTrigger>
            <TabsTrigger value="compliments" className="gap-2" data-testid="compliments-tab">
              <MessageSquareHeart className="w-4 h-4" />
              Compliments ({compliments.length})
            </TabsTrigger>
          </TabsList>
          <Button 
            variant="outline" 
            onClick={() => handleExport(activeTab)}
            className="gap-2"
            data-testid="export-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
        </div>

        {/* Complaints Tab */}
        <TabsContent value="complaints">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-red-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Complaint (Short)</TableHead>
                    <TableHead>Registration</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Action By</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Contacted</TableHead>
                    <TableHead>Satisfied</TableHead>
                    <TableHead>Docs</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {complaints.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                        No complaints found
                      </TableCell>
                    </TableRow>
                  ) : (
                    complaints.map((complaint) => (
                      <TableRow key={complaint.id} className={!complaint.satisfied ? 'bg-red-50/50' : ''}>
                        <TableCell className="font-mono text-sm">{complaint.date}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={complaint.complaint_short}>
                          {complaint.complaint_short}
                        </TableCell>
                        <TableCell className="font-mono">{complaint.registration_number || '-'}</TableCell>
                        <TableCell>{complaint.driver_name || getDriverName(complaint.driver_id)}</TableCell>
                        <TableCell>
                          <div className="text-sm">{complaint.client_name || '-'}</div>
                          {complaint.client_tel && (
                            <div className="text-xs text-slate-500">{complaint.client_tel}</div>
                          )}
                        </TableCell>
                        <TableCell>{complaint.action_by || '-'}</TableCell>
                        <TableCell className="max-w-[150px] truncate" title={complaint.outcome}>
                          {complaint.outcome || '-'}
                        </TableCell>
                        <TableCell>
                          {complaint.contacted ? (
                            <Badge className="bg-green-100 text-green-800">Yes</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">No</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {complaint.satisfied ? (
                            <Badge className="bg-green-100 text-green-800">Yes</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">No</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingDocuments({ type: 'complaint', record: complaint })}
                            className="gap-1"
                          >
                            <FileText className="w-4 h-4" />
                            {complaint.documents?.length || 0}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditComplaint(complaint)}
                              title="Edit"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteComplaint(complaint.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliments Tab */}
        <TabsContent value="compliments">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-green-50">
                    <TableHead>Date</TableHead>
                    <TableHead>Compliment (Short)</TableHead>
                    <TableHead>Registration</TableHead>
                    <TableHead>Driver</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Action By</TableHead>
                    <TableHead>Rewards</TableHead>
                    <TableHead>Actioned</TableHead>
                    <TableHead>Contacted</TableHead>
                    <TableHead>Docs</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {compliments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                        No compliments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    compliments.map((compliment) => (
                      <TableRow key={compliment.id} className={!compliment.actioned ? 'bg-amber-50/50' : ''}>
                        <TableCell className="font-mono text-sm">{compliment.date}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={compliment.compliment_short}>
                          {compliment.compliment_short}
                        </TableCell>
                        <TableCell className="font-mono">{compliment.registration_number || '-'}</TableCell>
                        <TableCell>{compliment.driver_name || getDriverName(compliment.driver_id)}</TableCell>
                        <TableCell>
                          <div className="text-sm">{compliment.client_name || '-'}</div>
                          {compliment.client_tel && (
                            <div className="text-xs text-slate-500">{compliment.client_tel}</div>
                          )}
                        </TableCell>
                        <TableCell>{compliment.action_by || '-'}</TableCell>
                        <TableCell className="max-w-[150px] truncate" title={compliment.rewards}>
                          {compliment.rewards || '-'}
                        </TableCell>
                        <TableCell>
                          {compliment.actioned ? (
                            <Badge className="bg-green-100 text-green-800">Yes</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {compliment.contacted ? (
                            <Badge className="bg-green-100 text-green-800">Yes</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">No</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingDocuments({ type: 'compliment', record: compliment })}
                            className="gap-1"
                          >
                            <FileText className="w-4 h-4" />
                            {compliment.documents?.length || 0}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCompliment(compliment)}
                              title="Edit"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCompliment(compliment.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Complaint Dialog */}
      <Dialog open={showComplaintDialog} onOpenChange={setShowComplaintDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <MessageSquareWarning className="w-5 h-5" />
              {editingRecord ? 'Edit Complaint' : 'New Complaint'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={complaintForm.date}
                onChange={(e) => setComplaintForm({ ...complaintForm, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Registration #</Label>
              <Select 
                value={complaintForm.registration_number || 'none'} 
                onValueChange={(v) => setComplaintForm({ ...complaintForm, registration_number: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {registrations.map(reg => (
                    <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Complaint (Short) *</Label>
              <Input
                value={complaintForm.complaint_short}
                onChange={(e) => setComplaintForm({ ...complaintForm, complaint_short: e.target.value })}
                placeholder="Brief summary of the complaint"
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Complaint (Full) *</Label>
              <Textarea
                value={complaintForm.complaint_full}
                onChange={(e) => setComplaintForm({ ...complaintForm, complaint_full: e.target.value })}
                placeholder="Full complaint details..."
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Driver</Label>
              <Select 
                value={complaintForm.driver_id || 'none'} 
                onValueChange={(v) => setComplaintForm({ ...complaintForm, driver_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>{getDriverName(d.id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Action By</Label>
              <Input
                value={complaintForm.action_by}
                onChange={(e) => setComplaintForm({ ...complaintForm, action_by: e.target.value })}
                placeholder="Person handling this complaint"
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Action Taken</Label>
              <Textarea
                value={complaintForm.action_taken}
                onChange={(e) => setComplaintForm({ ...complaintForm, action_taken: e.target.value })}
                placeholder="What action was taken to address the complaint..."
                rows={2}
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Outcome</Label>
              <Textarea
                value={complaintForm.outcome}
                onChange={(e) => setComplaintForm({ ...complaintForm, outcome: e.target.value })}
                placeholder="The outcome or resolution of the complaint..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input
                value={complaintForm.client_name}
                onChange={(e) => setComplaintForm({ ...complaintForm, client_name: e.target.value })}
                placeholder="Client name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Client Tel #</Label>
              <Input
                value={complaintForm.client_tel}
                onChange={(e) => setComplaintForm({ ...complaintForm, client_tel: e.target.value })}
                placeholder="Client phone number"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="contacted"
                checked={complaintForm.contacted}
                onCheckedChange={(checked) => setComplaintForm({ ...complaintForm, contacted: checked })}
              />
              <Label htmlFor="contacted" className="cursor-pointer">Client Contacted</Label>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="satisfied"
                checked={complaintForm.satisfied}
                onCheckedChange={(checked) => setComplaintForm({ ...complaintForm, satisfied: checked })}
              />
              <Label htmlFor="satisfied" className="cursor-pointer">Client Satisfied</Label>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => { setShowComplaintDialog(false); resetComplaintForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitComplaint} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingRecord ? 'Update Complaint' : 'Create Complaint'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compliment Dialog */}
      <Dialog open={showComplimentDialog} onOpenChange={setShowComplimentDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <MessageSquareHeart className="w-5 h-5" />
              {editingRecord ? 'Edit Compliment' : 'New Compliment'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={complimentForm.date}
                onChange={(e) => setComplimentForm({ ...complimentForm, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Registration #</Label>
              <Select 
                value={complimentForm.registration_number || 'none'} 
                onValueChange={(v) => setComplimentForm({ ...complimentForm, registration_number: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {registrations.map(reg => (
                    <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Compliment (Short) *</Label>
              <Input
                value={complimentForm.compliment_short}
                onChange={(e) => setComplimentForm({ ...complimentForm, compliment_short: e.target.value })}
                placeholder="Brief summary of the compliment"
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Compliment (Full) *</Label>
              <Textarea
                value={complimentForm.compliment_full}
                onChange={(e) => setComplimentForm({ ...complimentForm, compliment_full: e.target.value })}
                placeholder="Full compliment details..."
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Driver</Label>
              <Select 
                value={complimentForm.driver_id || 'none'} 
                onValueChange={(v) => setComplimentForm({ ...complimentForm, driver_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {drivers.map(d => (
                    <SelectItem key={d.id} value={d.id}>{getDriverName(d.id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Action By</Label>
              <Input
                value={complimentForm.action_by}
                onChange={(e) => setComplimentForm({ ...complimentForm, action_by: e.target.value })}
                placeholder="Person handling this compliment"
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Rewards</Label>
              <Textarea
                value={complimentForm.rewards}
                onChange={(e) => setComplimentForm({ ...complimentForm, rewards: e.target.value })}
                placeholder="Driver recognition or rewards given..."
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input
                value={complimentForm.client_name}
                onChange={(e) => setComplimentForm({ ...complimentForm, client_name: e.target.value })}
                placeholder="Client name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Client Tel #</Label>
              <Input
                value={complimentForm.client_tel}
                onChange={(e) => setComplimentForm({ ...complimentForm, client_tel: e.target.value })}
                placeholder="Client phone number"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="actioned"
                checked={complimentForm.actioned}
                onCheckedChange={(checked) => setComplimentForm({ ...complimentForm, actioned: checked })}
              />
              <Label htmlFor="actioned" className="cursor-pointer">Reward Actioned</Label>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="contacted2"
                checked={complimentForm.contacted}
                onCheckedChange={(checked) => setComplimentForm({ ...complimentForm, contacted: checked })}
              />
              <Label htmlFor="contacted2" className="cursor-pointer">Client Contacted</Label>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="satisfied2"
                checked={complimentForm.satisfied}
                onCheckedChange={(checked) => setComplimentForm({ ...complimentForm, satisfied: checked })}
              />
              <Label htmlFor="satisfied2" className="cursor-pointer">Client Satisfied</Label>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => { setShowComplimentDialog(false); resetComplimentForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitCompliment} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingRecord ? 'Update Compliment' : 'Create Compliment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={!!viewingDocuments} onOpenChange={() => setViewingDocuments(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documents
            </DialogTitle>
          </DialogHeader>
          
          {viewingDocuments && (
            <div className="space-y-4 mt-4">
              {/* Upload Section */}
              <div className="p-4 border rounded-lg bg-slate-50">
                <Label className="text-sm font-medium">Upload New Document</Label>
                <div className="flex gap-2 mt-2">
                  <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ca_document">CA Document</SelectItem>
                      <SelectItem value="action_document">Action Document</SelectItem>
                      <SelectItem value="evidence">Evidence</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleDocumentUpload}
                    className="hidden"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingDoc}
                    className="gap-2"
                  >
                    {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Upload
                  </Button>
                </div>
              </div>
              
              {/* Document List */}
              <div className="space-y-2">
                {viewingDocuments.record.documents?.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No documents uploaded</p>
                ) : (
                  viewingDocuments.record.documents?.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]">{doc.filename}</p>
                          <p className="text-xs text-slate-500">
                            {getDocumentTypeLabel(doc.document_type)} • {Math.round(doc.size / 1024)} KB
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadDocument(
                            viewingDocuments.type,
                            viewingDocuments.record.id,
                            doc.id,
                            doc.filename
                          )}
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(
                            viewingDocuments.type,
                            viewingDocuments.record.id,
                            doc.id
                          )}
                          className="text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplaintsPage;
